import * as React from "react";
import { CheckIcon, CopyIcon } from "../../../components/icons";
import { cn } from "../../../lib/utils";
import { copyText } from "../../../utils/copyText";

export interface MessageMetaProps {
  /** The model that wrote it. Assistant turns only. */
  model?: string;
  timestamp?: number;
  /** What Copy puts on the clipboard. No text, no Copy. */
  copyText?: string;
  /** User turns are right-aligned, so their footer is too. */
  align?: "start" | "end";
}

const formatTime = (timestamp: number) =>
  new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/** How long the tick replaces the copy glyph. */
const COPIED_MS = 1500;

/**
 * The footer under a turn: model, time and Copy, the web client's arrangement.
 *
 * Hidden until the turn is hovered or anything in it has focus -- it is reference
 * detail, and drawn on every turn it was a column of grey lines competing with the
 * replies. The parent carries Tailwind's `group`. On a device with no hover (iPad) it
 * is always shown, because "on hover" there would mean "never".
 *
 * The footer keeps its height while hidden, so revealing it never moves the chat.
 */
const MessageMeta = ({ model, timestamp, copyText: text, align = "start" }: MessageMetaProps) => {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const details = [model, timestamp ? formatTime(timestamp) : null].filter(Boolean).join(" · ");
  if (!details && !text) return null;

  return (
    <div
      data-testid="message-meta"
      className={cn(
        "flex min-h-[22px] min-w-0 items-center gap-1 text-caption text-muted-foreground opacity-0 transition-opacity duration-150 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100",
        align === "end" ? "flex-row-reverse" : "flex-row"
      )}
    >
      {text && (
        <button
          type="button"
          aria-label={copied ? "Copied" : "Copy message"}
          title={copied ? undefined : "Copy"}
          onClick={async () => setCopied(await copyText(text))}
          className="flex size-[22px] flex-none items-center justify-center rounded-md outline-none hover:bg-accent hover:text-strong focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          {copied ? (
            <CheckIcon className="size-3.5 text-success" strokeWidth={2.4} />
          ) : (
            <CopyIcon className="size-3.5" />
          )}
        </button>
      )}
      {details && <span className="min-w-0 truncate px-0.5">{details}</span>}
    </div>
  );
};

export default MessageMeta;
